import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { cn } from "../../lib/utils"

export interface MultiPickOption {
  label: string
  value: string
  icon?: React.ReactNode
}

export interface MultiPickProps {
  options: MultiPickOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  singlePick?: "designatedComponent"
  pickIfOne?: boolean
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  allowNewItem?: boolean
}

export function MultiPick({
  options,
  selected,
  onChange,
  singlePick,
  pickIfOne = false,
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  disabled = false,
  className,
  allowNewItem = false,
}: MultiPickProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [createdItems, setCreatedItems] = React.useState<MultiPickOption[]>([])

  const allOptions = React.useMemo(() => {
    const createdMap = new Map(createdItems.map((item) => [item.value, item]))
    const existingMap = new Map(options.map((item) => [item.value, item]))

    // Merge: created items take precedence, then existing options
    const merged = new Map([...createdMap, ...existingMap])
    return Array.from(merged.values())
  }, [options, createdItems])

  React.useEffect(() => {
    if (!pickIfOne) return
    if (allOptions.length !== 1) return
    if (selected.length !== 0) return

    const onlyValue = allOptions[0]?.value
    if (!onlyValue) return

    onChange([onlyValue])
  }, [pickIfOne, allOptions, selected, onChange])

  const handleToggle = (value: string) => {
    if (singlePick) {
      onChange([value])
      setOpen(false)
      return
    }
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const normalizedQuery = query.trim().toLowerCase()
  const hasMatches = normalizedQuery.length === 0
    ? true
    : allOptions.some((o) =>
      o.value.toLowerCase().includes(normalizedQuery) ||
      o.label.toLowerCase().includes(normalizedQuery)
    )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between hover:bg-transparent hover:text-foreground", className)}
          disabled={disabled}
        >
          {(() => {
            if (selected.length === 0) return placeholder
            if (singlePick) return allOptions.find((o) => o.value === selected[0])?.label
            if (selected.length === 1) {
              const one = allOptions.find((o) => o.value === selected[0])
              if (!one?.icon) {
                return one?.label ?? selected[0]
              }
              return (
                <span className="flex items-center gap-2">
                  <span className="flex h-4 w-4 items-center">{one.icon}</span>
                  <span>{one?.label ?? selected[0]}</span>
                </span>
              )
            }
            const concatenatedLabels = selected.map((s) => allOptions.find((o) => o.value === s)?.label ?? s).join(', ')
            if (concatenatedLabels.length < 30) {
              return concatenatedLabels
            }
            return `${selected.length} items`
          })()}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 rounded-lg" align="start">
        <Command>
          <CommandInput placeholder="Search..." value={query} onValueChange={setQuery} />
          <CommandList>
            {!(allowNewItem && normalizedQuery.length > 0 && !hasMatches) && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            <CommandGroup>
              {allowNewItem && query.trim().length > 0 && !hasMatches && (
                <CommandItem
                  key={`__create__${query}`}
                  value={query}
                  onSelect={() => {
                    const newValue = query.trim()
                    onChange(singlePick ? [newValue] : Array.from(new Set([...selected, newValue])))
                    // Add to created items permanently
                    const newItem: MultiPickOption = {
                      label: newValue,
                      value: newValue,
                    }
                    setCreatedItems((prev) => {
                      // Avoid duplicates
                      if (prev.some((item) => item.value === newValue)) {
                        return prev
                      }
                      return [...prev, newItem]
                    })
                    setQuery("")
                    setOpen(false)
                  }}
                  className="cursor-pointer hover:bg-transparent"
                  aria-checked={false}
                >
                  {!singlePick && (
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-border opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  <span>{`Create "${query.trim()}"`}</span>
                </CommandItem>
              )}
              {allOptions.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleToggle(option.value)}
                    className="cursor-pointer hover:bg-transparent"
                    aria-checked={isSelected}
                  >
                    {!singlePick && (
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-border",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                    {option.icon && <span className="mr-2 flex h-4 w-4 items-center justify-center">
                      {option.icon}
                    </span>}
                    <span>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
